// @ts-nocheck
import { Card, Code, Divider, Group, SimpleGrid, Stack, Text, Title } from '@/components/ui';
import { ContactCard, DealPipeline, ActivityTimeline } from '@/components/advanced/crm';

export default function CrmPage() {
  // ContactCard mock data
  const contact = {
    id: 'c1',
    firstName: 'Pam',
    lastName: 'Beesly',
    email: 'pam.beesly@dundermifflin.com',
    phone: '+1 (570) 555-0143',
    title: 'Office Administrator',
    company: 'Dunder Mifflin',
    city: 'Scranton',
    country: 'USA',
    status: 'customer',
    source: 'Referral',
    score: 82,
    starred: true,
    tags: ['vip', 'paper', 'enterprise'],
    notes: 'Long-standing account. Prefers email contact in the mornings.',
    lastContactedAt: new Date('2024-01-12'),
  };

  // DealPipeline mock data
  const pipelineStages = [
    { key: 'lead', label: 'Lead', color: 'gray', probability: 10 },
    { key: 'qualified', label: 'Qualified', color: 'blue', probability: 35 },
    { key: 'proposal', label: 'Proposal', color: 'orange', probability: 60 },
    { key: 'won', label: 'Closed Won', color: 'green', probability: 100 },
  ];

  const deals = [
    {
      id: 'd1',
      title: 'Annual paper supply contract',
      value: 48000,
      stage: 'qualified',
      probability: 40,
      priority: 'high',
      expectedCloseDate: new Date('2024-03-15'),
      contact: { id: 'c1', name: 'Pam Beesly', company: 'Dunder Mifflin' },
      owner: { id: 'u1', name: 'Jim Halpert' },
      tags: ['contract', 'recurring'],
    },
    {
      id: 'd2',
      title: 'Printer fleet upgrade',
      value: 21500,
      stage: 'proposal',
      probability: 65,
      priority: 'medium',
      expectedCloseDate: new Date('2024-02-28'),
      contact: { id: 'c2', name: 'Angela Martin', company: 'Dunder Mifflin' },
      owner: { id: 'u2', name: 'Dwight Schrute' },
      tags: ['hardware'],
    },
    {
      id: 'd3',
      title: 'New regional account',
      value: 9000,
      stage: 'lead',
      probability: 15,
      priority: 'low',
      contact: { id: 'c3', name: 'Bob Vance', company: 'Vance Refrigeration' },
      owner: { id: 'u1', name: 'Jim Halpert' },
    },
    {
      id: 'd4',
      title: 'Q4 reorder',
      value: 33000,
      stage: 'won',
      probability: 100,
      expectedCloseDate: new Date('2024-01-05'),
      contact: { id: 'c1', name: 'Pam Beesly', company: 'Dunder Mifflin' },
      owner: { id: 'u2', name: 'Dwight Schrute' },
      tags: ['recurring'],
    },
  ];

  // ActivityTimeline mock data
  const activities = [
    {
      id: 'a1',
      type: 'call',
      title: 'Discovery call with Pam',
      description: 'Reviewed renewal terms and confirmed budget for next quarter.',
      timestamp: new Date('2024-01-12T10:30:00'),
      status: 'completed',
      duration: 35,
      outcome: 'Positive — ready to move to proposal stage.',
      user: { id: 'u1', name: 'Jim Halpert' },
      contact: { id: 'c1', name: 'Pam Beesly' },
      deal: { id: 'd1', title: 'Annual paper supply contract' },
    },
    {
      id: 'a2',
      type: 'email',
      title: 'Sent updated proposal',
      description: 'Emailed the revised pricing sheet with the volume discount applied.',
      timestamp: new Date('2024-01-12T14:05:00'),
      status: 'completed',
      user: { id: 'u2', name: 'Dwight Schrute' },
      contact: { id: 'c2', name: 'Angela Martin' },
      deal: { id: 'd2', title: 'Printer fleet upgrade' },
    },
    {
      id: 'a3',
      type: 'meeting',
      title: 'On-site demo',
      timestamp: new Date('2024-01-11T09:00:00'),
      status: 'pending',
      duration: 60,
      user: { id: 'u1', name: 'Jim Halpert' },
      contact: { id: 'c3', name: 'Bob Vance' },
    },
  ];

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>CRM Components</Title>
        <Text c="dimmed" size="sm">
          Components for managing contacts, sales pipelines, and customer activity in a CRM workflow
        </Text>
      </Stack>

      {/* ContactCard */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>ContactCard</Title>
            <Text size="sm" c="dimmed">
              Displays a contact with avatar, status, tags, and quick actions. Supports several
              variants.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>
              Example:
            </Text>
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
              <Stack gap="xs">
                <Text size="xs" c="dimmed">
                  variant="default"
                </Text>
                <ContactCard
                  contact={contact}
                  variant="default"
                  showTags
                  onEmail={() => {}}
                  onCall={() => {}}
                  onEdit={() => {}}
                />
              </Stack>
              <Stack gap="xs">
                <Text size="xs" c="dimmed">
                  variant="compact"
                </Text>
                <ContactCard contact={contact} variant="compact" onEdit={() => {}} />
              </Stack>
            </SimpleGrid>

            <Text size="xs" c="dimmed">
              variant="detailed"
            </Text>
            <ContactCard
              contact={contact}
              variant="detailed"
              showTags
              showScore
              onEmail={() => {}}
              onCall={() => {}}
              onSchedule={() => {}}
              onToggleStar={() => {}}
              onEdit={() => {}}
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Code:
            </Text>
            <Code block language="tsx">
{`const contact = {
  id: 'c1',
  firstName: 'Pam',
  lastName: 'Beesly',
  email: 'pam.beesly@dundermifflin.com',
  phone: '+1 (570) 555-0143',
  title: 'Office Administrator',
  company: 'Dunder Mifflin',
  status: 'customer',
  score: 82,
  starred: true,
  tags: ['vip', 'paper', 'enterprise'],
};

<ContactCard
  contact={contact}
  variant="detailed"
  showTags
  showScore
  onEmail={() => {}}
  onCall={() => {}}
  onEdit={() => {}}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Key Props:
            </Text>
            <Text size="xs" c="dimmed" component="div">
              • contact - ContactData object (firstName, lastName required)
              <br />
              • variant - 'default' | 'compact' | 'detailed' | 'horizontal'
              <br />
              • showActions / showTags / showScore - Toggle sections
              <br />
              • onEmail / onCall / onSchedule / onEdit / onToggleStar - Action handlers
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* DealPipeline */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>DealPipeline</Title>
            <Text size="sm" c="dimmed">
              Drag-and-drop sales pipeline with stage totals, weighted values, and per-deal cards.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>
              Example:
            </Text>
            <DealPipeline
              stages={pipelineStages}
              deals={deals}
              currency="USD"
              showTotalValue
              showProbability
              onDealMove={() => {}}
              onDealClick={() => {}}
              onDealEdit={() => {}}
              onAddDeal={() => {}}
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Code:
            </Text>
            <Code block language="tsx">
{`const stages = [
  { key: 'lead', label: 'Lead', color: 'gray', probability: 10 },
  { key: 'qualified', label: 'Qualified', color: 'blue', probability: 35 },
  { key: 'proposal', label: 'Proposal', color: 'orange', probability: 60 },
  { key: 'won', label: 'Closed Won', color: 'green', probability: 100 },
];

const deals = [
  {
    id: 'd1',
    title: 'Annual paper supply contract',
    value: 48000,
    stage: 'qualified',
    probability: 40,
    priority: 'high',
    expectedCloseDate: new Date('2024-03-15'),
    contact: { id: 'c1', name: 'Pam Beesly', company: 'Dunder Mifflin' },
    owner: { id: 'u1', name: 'Jim Halpert' },
    tags: ['contract', 'recurring'],
  },
  // ... more deals
];

<DealPipeline
  stages={stages}
  deals={deals}
  currency="USD"
  onDealMove={(dealId, fromStage, toStage) => {
    // Handle stage change
  }}
  onDealClick={(deal) => console.log('Deal clicked:', deal)}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Key Props:
            </Text>
            <Text size="xs" c="dimmed" component="div">
              • stages - Array of PipelineStage (key, label, color, probability)
              <br />
              • deals - Array of Deal (id, title, value, stage required)
              <br />
              • currency - ISO currency code (default 'USD')
              <br />
              • showTotalValue / showProbability - Toggle summary + progress
              <br />
              • onDealMove / onDealClick / onDealEdit / onDealDelete / onAddDeal - Handlers
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* ActivityTimeline */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>ActivityTimeline</Title>
            <Text size="sm" c="dimmed">
              Chronological feed of customer interactions (calls, emails, meetings) grouped by date.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>
              Example:
            </Text>
            <ActivityTimeline
              activities={activities}
              groupByDate
              showActions
              onActivityClick={() => {}}
              onActivityEdit={() => {}}
              onAddActivity={() => {}}
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Code:
            </Text>
            <Code block language="tsx">
{`const activities = [
  {
    id: 'a1',
    type: 'call',
    title: 'Discovery call with Pam',
    description: 'Reviewed renewal terms and confirmed budget.',
    timestamp: new Date('2024-01-12T10:30:00'),
    status: 'completed',
    duration: 35,
    outcome: 'Positive — ready to move to proposal stage.',
    user: { id: 'u1', name: 'Jim Halpert' },
    contact: { id: 'c1', name: 'Pam Beesly' },
    deal: { id: 'd1', title: 'Annual paper supply contract' },
  },
  // ... more activities
];

<ActivityTimeline
  activities={activities}
  groupByDate
  onActivityClick={(activity) => console.log(activity)}
  onAddActivity={(type) => console.log('Log', type)}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Activity Interface:
            </Text>
            <Code block language="typescript">
{`interface Activity {
  id: string;
  type: 'email' | 'call' | 'meeting' | 'note' | 'message' | 'file' | 'task' | 'custom';
  title: string;
  timestamp: Date;
  description?: string;
  status?: 'completed' | 'pending' | 'cancelled';
  duration?: number; // minutes
  outcome?: string;
  user?: { id: string; name: string; avatar?: string };
  contact?: { id: string; name: string };
  deal?: { id: string; title: string };
}`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Common Use Cases</Title>
          <Divider />

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Contact Management
              </Text>
              <Text size="xs" c="dimmed">
                Use ContactCard in directories and detail panels to surface contact info, lead
                scores, and quick actions like email or call.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Sales Pipeline
              </Text>
              <Text size="xs" c="dimmed">
                Use DealPipeline to track opportunities across stages, monitor weighted forecast
                value, and move deals via drag-and-drop.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Engagement History
              </Text>
              <Text size="xs" c="dimmed">
                Use ActivityTimeline on a contact or deal record to show the full history of calls,
                emails, and meetings.
              </Text>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Card>
    </Stack>
  );
}
